import React from 'react';

import {
  currencyString,
  formatDate,
  isFutureDate,
  PAYMENT_METHODS,
  PaymentDetailsT,
  today,
} from '@consumer-frontend/nc-utils';
import { Container, Fieldset } from '@consumer-frontend/shared-ui';

import { NcReviewPaymentFields } from './NcReviewPaymentFields';
import { NcButton } from '../NcButton';
import { NcReviewFields } from '../NcReviewFields';
import { NcSupportNumberLink } from '../NcSupportNumberLink';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/payment/review.scss';

export type NcReviewPaymentDetailT = React.FC<{
  onAuthorizePayment: () => void;
  onEditPayment: () => void;
  paymentDetails: PaymentDetailsT;
  isLoading?: boolean;
}>;

export const NcReviewPaymentDetail: NcReviewPaymentDetailT = ({
  isLoading,
  onAuthorizePayment,
  onEditPayment,
  paymentDetails,
}) => {
  const {
    paymentAmount,
    paymentDate,
    paymentMethod,
    paymentOwner,
  } = paymentDetails;
  const formattedCurrentDate = <span>{formatDate(today())}</span>;
  const formattedPaymentDate = <span>{formatDate(paymentDate)}</span>;

  const paymentOwnerText = paymentOwner === 'NetCredit' ? '' : `${paymentOwner}, c/o`;

  const authorizationText = (
    <>
      {paymentMethod === PAYMENT_METHODS.DEBIT_CARD && (
        <>
          <p data-chromatic="ignore">
            On {formattedCurrentDate}, you voluntarily authorize us, {paymentOwner}{' '}
            including our agents, service providers, successors and assigns, to initiate a one-time electronic payment from
            the debit card specified above in the amount of {currencyString(paymentAmount)} on {formattedPaymentDate}.
          </p>
          {isFutureDate(paymentDate) && (
            <p>
              You may revoke this debit card payment authorization by contacting the NetCredit Customer
              Support Team at <NcSupportNumberLink />, by email at{' '}
              <a className="non-overflowing" href="mailto:support@netcredit.com">support@netcredit.com</a>,{' '}
              or by mail at {paymentOwner}, c/o NetCredit,
              175 W. Jackson Blvd., Suite 600, Chicago, IL 60604. We must receive your instruction to revoke
              this authorization at least two (2) Business Days before the scheduled payment.
            </p>
          )}
          {!isFutureDate(paymentDate) && (
            <p>
              Please note that it may take up to the end of the day to process your payment. Since this payment is to be
              processed immediately, you cannot cancel or revoke once it is authorized. You will receive an email
              confirmation when your payment has been successfully processed. If the payment is not successful, your
              balance will be updated to reflect that. You may contact us Monday – Friday, 8am – 8pm CT or Saturday -
              Sunday, 9am – 5:30pm CT at <NcSupportNumberLink /> for any future inquiries.
            </p>
          )}
        </>
      )}
      {paymentMethod === PAYMENT_METHODS.ACH && (
        <>
          <p data-chromatic="ignore">
            On {formattedCurrentDate}, you voluntarily authorize us, {paymentOwner}{' '}
            including our agents, service providers, successors and assigns, to initiate a one-time ACH payment from the
            bank account specified above on {formattedPaymentDate} in the amount of {currencyString(paymentAmount)}.
          </p>
          <p>
            You may revoke this authorization by contacting the NetCredit Customer Support Team at{' '}
            <NcSupportNumberLink />, by email at{' '}
            <a href="mailto:support@netcredit.com">support@netcredit.com</a>, or by mail at {paymentOwnerText}{' '}
            NetCredit, 175 W. Jackson Blvd., Suite 600, Chicago, IL 60604. We must receive your instruction to revoke this
            authorization at least two (2) Business Days before the scheduled payment.
          </p>
        </>
      )}
    </>
  );

  return (
    <Container className="payment-review-page" hasBoxShadow>
      <h1 className="h2">Review Payment Details</h1>

      <NcReviewPaymentFields {...paymentDetails} />

      <NcReviewFields fields={[{ label: 'Authorization', value: authorizationText }]} />

      <Fieldset className="cta-buttons">
        <NcButton
          variant="primary"
          onClick={onAuthorizePayment}
          loading={isLoading}
          disabled={isLoading}
        >
          Authorize Payment
        </NcButton>

        <NcButton
          variant="outline-primary"
          onClick={onEditPayment}
          disabled={isLoading}
        >
          Edit Payment Details
        </NcButton>
      </Fieldset>
    </Container>
  );
};
